import { useCallback, useEffect, useMemo, useState } from "react";
import {
  noBarData,
  noData,
  validLineData,
  totalActivity,
  chartColorScale,
  dailyActivity,
  createDateRange,
  getRateString,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { ChartWrapper, getDateFormatByCount, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";

import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { useQuery } from "@tanstack/react-query";
import { fetchMessageSessions, getEngagementQuery, getLineChartQuery } from "./Queries/queries";

import styles from "./messagePerformanceBody.module.scss";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { bindAction } from "interfaces";
import { setHasDataToExport, setExportAction } from "features/Reporting/state/export/exportSlice";
import { bindActionCreators } from "@reduxjs/toolkit";
import { type ColumnOption } from "interfaces/columnOptions";
import { type MessageSession } from "../types/models";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { MessageStatusToIcon, MessageStatusToColor, MessageStatusToString, type MessageStatuses } from "./types";
import { doMessageExport } from "../state/thunks/messagePerformanceThunk";
import MessagePerformanceDrillDownBody from "./MessagePerformanceDrillDownBody";
import type { RegisterBreadcrumbCallback } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { type PaginationParams } from "features/Library/Common/models";
import { reset, setIsReportEnabled } from "features/Reporting/state/toolbar/toolbarSlice";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";

export type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
  messageId: number;
  messageName: string;
  includeAccounts: boolean;
  registerBreadcrumb: RegisterBreadcrumbCallback;
};

type SessionColumnOption = ColumnOption<MessageSession> & { sortKey?: string };

const getColumnOptions = (
  formatDateTime: (date: string) => string,
  handleSessionSelection: (sendSessionId: number, sessionName: string) => void,
): SessionColumnOption[] => {
  return [
    {
      name: "Session",
      width: 4,
      isSortable: true,
      sortKey: "SessionDatetime",
      render(entity) {
        const dateTime = formatDateTime(entity.SessionDatetime);
        return (
          <div>
            <Tooltip
              target={
                <TextTruncate
                  className={styles.invisibleButton}
                  onClick={() => handleSessionSelection(entity.SessionId, dateTime)}
                >
                  {dateTime}
                </TextTruncate>
              }
              content={dateTime}
              position="left center"
            />
          </div>
        );
      },
    },
    {
      isSortable: true,
      name: "Origin",
      sortKey: "Origin",
      render(entity) {
        return entity.Origin;
      },
      width: 3,
    },
    {
      isSortable: true,
      name: "Sends",
      sortKey: "Sends",
      render(entity) {
        return entity.Sends;
      },
      width: 3,
    },
    {
      isSortable: true,
      name: "Clicks",
      sortKey: "Clicks",
      render(entity) {
        return entity.Clicks;
      },
      width: 3,
    },
    {
      isSortable: true,
      name: "CTR",
      sortKey: "ClickThroughRate",
      render: (entity) => getRateString(entity.ClickThroughRate),
      width: 3,
    },
    {
      isSortable: true,
      name: "Status",
      sortKey: "Status",
      render(entity) {
        const status = entity.Status as MessageStatuses;
        return (
          <div className={styles.status}>
            <FontAwesomeIcon icon={MessageStatusToIcon[status]} className={styles[MessageStatusToColor[status]]} />
            {MessageStatusToString[status]}
          </div>
        );
      },
      width: 3,
    },
  ];
};

export const MessagePerformanceBody: React.FC<Props> = ({
  filter,
  messageId,
  messageName,
  setHasExportData,
  setExportAction,
  messageExport,
  includeAccounts,
  registerBreadcrumb,
  resetIsReportEnabled,
  setIsReportEnabled,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const [selectedSendSessionId, setSelectedSendSessionId] = useState<number | undefined>(undefined);
  const [selectedSendSessionDateTime, setSelectedSendSessionDateTime] = useState<string | undefined>(undefined);
  const formatDateTime = useReportingFormatWithTimeZone();
  const isReportEnabled = useFeatureFlag(FeatureFlags.MessageReport);
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["messageLineChart", filter, messageId, chartPeriod],
    queryFn: getLineChartQuery,
    enabled: chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["messageEngagement", filter, messageId],
    queryFn: getEngagementQuery,
    enabled: filter.type !== undefined,
  });

  useEffect(() => {
    setIsReportEnabled(isReportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [isReportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const handleSessionSelection = useCallback(
    (sendSessionId: number, sessionName: string) => {
      setSelectedSendSessionId(sendSessionId);
      setSelectedSendSessionDateTime(sessionName);
      registerBreadcrumb({
        text: sessionName,
        action: () => {
          setSelectedSendSessionId(undefined);
        },
      });
    },
    [registerBreadcrumb],
  );

  const columnOptions = useMemo(
    () => getColumnOptions(formatDateTime, handleSessionSelection),
    [formatDateTime, handleSessionSelection],
  );

  const sessionsQueryKey = useMemo(() => ["messageSessions", filter, messageId] as const, [filter, messageId]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This message contains no data.",
      }),
    [setHasExportData],
  );

  const fetchSessionsData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => fetchMessageSessions(filter, messageId, params, signal),
    [filter, messageId],
  );

  useEffect(() => {
    if (!selectedSendSessionId) {
      setExportAction({
        method: messageExport,
        args: [{ ...filter, messageId }, messageName],
        isExportEnabled: true,
      });
    }
  }, [filter, messageExport, setExportAction, messageId, messageName, selectedSendSessionId]);

  const lineData = useMemo(() => {
    if (chartState === dailyActivity) {
      return {
        xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
        yData: [lineChartQuery.data?.Sends, lineChartQuery.data?.Clicks],
      };
    }
    return {
      xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
      yData: [lineChartQuery.data?.CumulativeSends, lineChartQuery.data?.CumulativeClicks],
    };
  }, [chartState, lineChartQuery.data]);

  if (!isReportEnabled) {
    return <ReportUnavailable />;
  }

  if (selectedSendSessionId && selectedSendSessionDateTime) {
    return (
      <MessagePerformanceDrillDownBody
        filter={filter}
        messageId={messageId}
        messageName={messageName}
        selectedSendSessionId={selectedSendSessionId}
        selectedSendSessionDateTime={selectedSendSessionDateTime}
        includeAccounts={includeAccounts}
      />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Total Activity", "Daily Activity"]}
          legendLabels={["Sends", "Clicks"]}
          showLegend
          onChange={setChartState}
          colorRange={chartColorScale}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([lineChartQuery.data.CumulativeSends, lineChartQuery.data.CumulativeClicks]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                colorRange={chartColorScale}
                {...lineData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.engagementQuery}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.usageBarData.map((d) => d.value)) ? (
              <HorizontalBarChart
                {...sharedAccountReportingHorizontalBarProps}
                data={engagementQuery.data.usageBarData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <GenericPerformanceListWithServerPagination
          title="Sends"
          columns={columnOptions}
          filter={filter}
          fetchPage={fetchSessionsData}
          queryKeyBase={sessionsQueryKey}
          hasDataForExport={updateExportDataState}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (_state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  messageExport: bindActionCreators(doMessageExport, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(MessagePerformanceBody);
export default ConnectedComponent;
